import React, { useRef, type PropsWithChildren } from "react";
import styles from "./LoadingState.module.scss";
import classNames from "classnames/bind";
import { IconLoading } from "../Icon";
import { type LoadingStateProps, type LoadingStateSizeType } from "./types";
import colours from "../constants/colours";
import { nanoid } from "nanoid";

const cx = classNames.bind(styles);

/**
 * Use this component to indicate to the user that you cannot show an area of UI yet till everything required to render it is ready
 */
export const LoadingState = ({
  label,
  indicator,
  size = "normal",
  isLoading = true,
  children,
}: PropsWithChildren<LoadingStateProps>) => {
  const labelId = useRef(nanoid());
  const iconSizes: Record<LoadingStateSizeType, number> = {
    small: 24,
    normal: 32,
    large: 44,
  };
  const hintIcon = (
    <IconLoading
      size={iconSizes[size]}
      colour={colours.colours.text[600]}
    />
  );

  if (!isLoading) {
    return <>{children}</>;
  }

  return (
    <div
      className={cx("container", size)}
      aria-busy
      aria-live="polite"
      role="status"
      aria-labelledby={labelId.current}
    >
      <div
        className={cx("indicatorContainer")}
        id={labelId.current}
      >
        {indicator ? indicator : hintIcon}
      </div>

      <p className={cx("label", size)}>{label}</p>
    </div>
  );
};
